import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  computed,
  effect,
  input,
  linkedSignal,
  resource,
  inject,
} from '@angular/core';
import { LoadingIndicatorComponent } from '@frontendlib/components/components/loading-indicator/loading-indicator.component';
import { Button2Component } from '@frontendlib/components/components/button/button2.component';
import { PreviewErrorComponent } from '../preview-error/preview-error.component';
import { TopProgressService } from '@modules/top-progress/top-progress.service';

interface SheetPreview {
  readonly name: string;
  readonly html: string;
}

@Component({
  selector: 'app-spreadsheet-preview',
  templateUrl: './spreadsheet-preview.component.html',
  styleUrl: './spreadsheet-preview.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [LoadingIndicatorComponent, Button2Component, PreviewErrorComponent],
})
export class SpreadsheetPreviewComponent {
  public readonly blob = input<Blob | null>(null);
  private readonly topProgress = inject(TopProgressService);

  protected readonly sheets = resource<ReadonlyArray<SheetPreview>, Blob | null>({
    params: () => this.blob(),
    defaultValue: [],
    loader: async ({ params: blob, abortSignal }) => {
      if (!blob) {
        return [];
      }
      const [buffer, xlsx] = await Promise.all([blob.arrayBuffer(), this.topProgress.track(import('xlsx'))]);
      abortSignal.throwIfAborted();
      const workbook = xlsx.read(buffer, { type: 'array' });
      return workbook.SheetNames.map((name) => ({
        name,
        html: xlsx.utils.sheet_to_html(workbook.Sheets[name], { header: '', footer: '' }),
      }));
    },
  });

  // Resets to 0 whenever a new workbook loads, but is freely writable when a tab is clicked.
  protected readonly activeSheetIndex = linkedSignal({
    source: () => this.sheets.value(),
    computation: () => 0,
  });
  protected readonly activeSheet = computed(() => this.sheets.value()[this.activeSheetIndex()]);

  public constructor() {
    effect(() => {
      const err = this.sheets.error();
      if (err) {
        console.warn('[spreadsheet-preview] render failed', err);
      }
    });
  }

  protected selectSheet(index: number): void {
    this.activeSheetIndex.set(index);
  }
}
